import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { LeaseFormData, LeaseDocument } from '../types/lease.types';

// Utility function to remove undefined values from objects (Firestore doesn't allow undefined)
const cleanData = (obj: any): any => {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj === undefined ? null : obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(cleanData);
  }
  
  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = cleanData(value);
    }
  }
  return cleaned;
};

export const saveLeaseAgreement = async (leaseData: LeaseFormData, userId: string, leaseName?: string): Promise<string> => {
  try {
    const leaseRef = doc(collection(db, 'leases'));
    const leaseId = leaseRef.id;
    
    const leaseDocument: Partial<LeaseDocument> = {
      ...cleanData(leaseData),
      id: leaseId,
      name: leaseName || `Lease Draft - ${new Date().toLocaleDateString()}`,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'draft',
      version: 1,
    };
    
    await setDoc(leaseRef, leaseDocument);
    return leaseId;
  } catch (error) {
    console.error('Error saving lease:', error);
    throw new Error('Failed to save lease agreement');
  }
};

export const saveDraftLease = async (leaseData: Partial<LeaseFormData>, userId: string, leaseId?: string, leaseName?: string): Promise<string> => {
  try {
    const cleanedData = cleanData(leaseData);
    
    if (leaseId) {
      // Update existing draft
      const leaseRef = doc(db, 'leases', leaseId);
      await updateDoc(leaseRef, {
        ...cleanedData,
        name: leaseName || cleanedData.name || `Lease Draft - ${new Date().toLocaleDateString()}`,
        updatedAt: serverTimestamp(),
        status: 'draft',
      });
      return leaseId;
    } else {
      // Create new draft
      const leaseRef = doc(collection(db, 'leases'));
      const newLeaseId = leaseRef.id;
      
      const leaseDocument: Partial<LeaseDocument> = {
        ...cleanedData,
        id: newLeaseId,
        name: leaseName || `Lease Draft - ${new Date().toLocaleDateString()}`,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft',
        version: 1,
      };
      
      await setDoc(leaseRef, leaseDocument);
      return newLeaseId;
    }
  } catch (error) {
    console.error('Error saving draft lease:', error);
    throw new Error('Failed to save draft lease');
  }
};

export const updateLeaseAgreement = async (leaseId: string, leaseData: Partial<LeaseFormData>, leaseName?: string): Promise<void> => {
  try {
    const leaseRef = doc(db, 'leases', leaseId);
    const updateData: any = {
      ...cleanData(leaseData),
      updatedAt: serverTimestamp(),
    };
    
    if (leaseName) {
      updateData.name = leaseName;
    }
    
    await updateDoc(leaseRef, updateData);
  } catch (error) {
    console.error('Error updating lease:', error);
    throw new Error('Failed to update lease agreement');
  }
};

export const updateLeaseStatus = async (leaseId: string, newStatus: 'draft' | 'active' | 'terminated'): Promise<void> => {
  try {
    const leaseRef = doc(db, 'leases', leaseId);
    const updateData: any = {
      status: newStatus,
      updatedAt: serverTimestamp(),
    };
    
    // Add termination date if status is being set to terminated
    if (newStatus === 'terminated') {
      updateData.terminationDate = new Date().toISOString();
    }
    
    await updateDoc(leaseRef, updateData);
  } catch (error) {
    console.error('Error updating lease status:', error);
    throw new Error('Failed to update lease status');
  }
};

export const getLeaseAgreement = async (leaseId: string): Promise<LeaseDocument | null> => {
  try {
    const leaseRef = doc(db, 'leases', leaseId);
    const leaseSnap = await getDoc(leaseRef);
    
    if (leaseSnap.exists()) {
      const data = leaseSnap.data();
      // Convert Firestore timestamps to ISO strings
      return {
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
      } as LeaseDocument;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting lease:', error);
    throw new Error('Failed to retrieve lease agreement');
  }
};

export const getUserLeases = async (userId: string): Promise<LeaseDocument[]> => {
  try {
    const leasesRef = collection(db, 'leases');
    // Temporary: Simple query without orderBy to avoid index requirement
    const q = query(
      leasesRef, 
      where('createdBy', '==', userId),
      limit(50)
    );
    
    const querySnapshot = await getDocs(q);
    const leases: LeaseDocument[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      leases.push({
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
      } as LeaseDocument);
    });
    
    // Sort in JavaScript instead of Firestore (temporary solution)
    return leases.sort((a, b) => {
      const aTime = new Date(a.updatedAt || 0).getTime();
      const bTime = new Date(b.updatedAt || 0).getTime();
      return bTime - aTime; // Descending order (newest first)
    });
  } catch (error) {
    console.error('Error getting user leases:', error);
    throw new Error('Failed to retrieve user leases');
  }
};

export const generateLeaseDocument = async (leaseId: string, leaseName?: string): Promise<{ blob: Blob; leaseId: string }> => {
  try {
    // First, update the lease name if provided
    if (leaseName) {
      await updateLeaseAgreement(leaseId, {}, leaseName);
    }
    
    // Get the lease data
    const leaseData = await getLeaseAgreement(leaseId);
    if (!leaseData) {
      throw new Error('Lease not found');
    }
    
    // Import the PDF generator dynamically to avoid circular dependencies
    const { generateLeasePDFEnhanced } = await import('../utils/pdfGenerator');
    const blob = await generateLeasePDFEnhanced(leaseData);
    
    // Update the lease status to active after generation
    await updateDoc(doc(db, 'leases', leaseId), {
      status: 'active',
      updatedAt: serverTimestamp(),
    });
    
    return { blob, leaseId };
  } catch (error) {
    console.error('Error generating lease document:', error);
    throw new Error('Failed to generate lease document');
  }
};

export const signLease = async (leaseId: string, signatureData: {
  signerName: string;
  signerType: 'landlord' | 'tenant';
  ipAddress: string;
}): Promise<void> => {
  try {
    const leaseRef = doc(db, 'leases', leaseId);
    const lease = await getLeaseAgreement(leaseId);
    
    if (!lease) {
      throw new Error('Lease not found');
    }
    
    const signature = {
      name: signatureData.signerName,
      date: new Date().toISOString(),
      ip: signatureData.ipAddress,
      method: 'electronic' as const,
    };
    
    const signatures = lease.signatures || { 
      landlord: {
        name: '',
        date: '',
        ip: '',
        method: 'electronic' as const,
      }, 
      tenants: [] 
    };
    
    if (signatureData.signerType === 'landlord') {
      signatures.landlord = signature;
    } else {
      signatures.tenants.push(signature);
    }
    
    await updateDoc(leaseRef, {
      signatures,
      updatedAt: serverTimestamp(),
      status: 'active',
    });
  } catch (error) {
    console.error('Error signing lease:', error);
    throw new Error('Failed to sign lease');
  }
};

export const terminateLease = async (leaseId: string, terminationDate: string, reason?: string): Promise<void> => {
  try {
    const leaseRef = doc(db, 'leases', leaseId);
    await updateDoc(leaseRef, {
      status: 'terminated',
      terminationDate,
      terminationReason: reason,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error terminating lease:', error);
    throw new Error('Failed to terminate lease');
  }
};

export const deleteLease = async (leaseId: string): Promise<void> => {
  try {
    const leaseRef = doc(db, 'leases', leaseId);
    await updateDoc(leaseRef, {
      status: 'deleted',
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error deleting lease:', error);
    throw new Error('Failed to delete lease');
  }
}; 